import React from "react";
import "../styles/AppErrorBoundary.css";

const ERROR_COPY = {
  en: {
    title: "This page could not be loaded temporarily",
    text: "Your data has not been lost. Refresh the page or return to the home page.",
    reload: "Refresh",
    home: "Home",
  },
  ru: {
    title: "Страница временно не загрузилась",
    text: "Данные не потеряны. Обновите страницу или вернитесь на главную.",
    reload: "Обновить",
    home: "На главную",
  },
  de: {
    title: "Diese Seite konnte vorübergehend nicht geladen werden",
    text: "Ihre Daten sind nicht verloren. Laden Sie die Seite neu oder kehren Sie zur Startseite zurück.",
    reload: "Neu laden",
    home: "Startseite",
  },
  uk: {
    title: "Сторінка тимчасово не завантажилася",
    text: "Дані не втрачено. Оновіть сторінку або поверніться на головну.",
    reload: "Оновити",
    home: "На головну",
  },
  es: {
    title: "Esta página no se pudo cargar temporalmente",
    text: "Tus datos no se han perdido. Actualiza la página o vuelve al inicio.",
    reload: "Actualizar",
    home: "Inicio",
  },
  pt: {
    title: "Esta página não pôde ser carregada temporariamente",
    text: "Os seus dados não foram perdidos. Atualize a página ou volte à página inicial.",
    reload: "Atualizar",
    home: "Início",
  },
  fr: {
    title: "Cette page n’a pas pu être chargée temporairement",
    text: "Vos données n’ont pas été perdues. Actualisez la page ou revenez à l’accueil.",
    reload: "Actualiser",
    home: "Accueil",
  },
  it: {
    title: "Questa pagina non è stata caricata temporaneamente",
    text: "I tuoi dati non sono andati persi. Aggiorna la pagina o torna alla home.",
    reload: "Aggiorna",
    home: "Home",
  },
  pl: {
    title: "Nie udało się tymczasowo załadować tej strony",
    text: "Twoje dane nie zostały utracone. Odśwież stronę lub wróć na stronę główną.",
    reload: "Odśwież",
    home: "Strona główna",
  },
  tr: {
    title: "Bu sayfa geçici olarak yüklenemedi",
    text: "Verileriniz kaybolmadı. Sayfayı yenileyin veya ana sayfaya dönün.",
    reload: "Yenile",
    home: "Ana sayfa",
  },
  id: {
    title: "Halaman ini sementara tidak dapat dimuat",
    text: "Data Anda tidak hilang. Muat ulang halaman atau kembali ke beranda.",
    reload: "Muat ulang",
    home: "Beranda",
  },
  hi: {
    title: "यह पेज अस्थायी रूप से लोड नहीं हो सका",
    text: "आपका डेटा खोया नहीं है। पेज रीफ़्रेश करें या होम पेज पर लौटें।",
    reload: "रीफ़्रेश करें",
    home: "होम",
  },
  ko: {
    title: "이 페이지를 일시적으로 불러오지 못했습니다",
    text: "데이터는 손실되지 않았습니다. 페이지를 새로고침하거나 홈으로 돌아가세요.",
    reload: "새로고침",
    home: "홈",
  },
  ja: {
    title: "このページを一時的に読み込めませんでした",
    text: "データは失われていません。ページを再読み込みするか、ホームに戻ってください。",
    reload: "再読み込み",
    home: "ホーム",
  },
  zh: {
    title: "此页面暂时无法加载",
    text: "您的数据没有丢失。请刷新页面或返回首页。",
    reload: "刷新",
    home: "首页",
  },
  ar: {
    title: "تعذّر تحميل هذه الصفحة مؤقتًا",
    text: "لم تُفقد بياناتك. حدّث الصفحة أو عُد إلى الصفحة الرئيسية.",
    reload: "تحديث",
    home: "الرئيسية",
  },
};

const getErrorLanguage = () => {
  if (typeof window === "undefined") return "en";

  const hostname = String(window.location.hostname || "").toLowerCase();

  if (hostname === "mapapet.com" || hostname === "www.mapapet.com") {
    return "en";
  }

  const subdomain = hostname.split(".")[0];
  if (ERROR_COPY[subdomain]) return subdomain;

  const browserLanguage = String(navigator.language || "en")
    .toLowerCase()
    .split("-")[0];

  return ERROR_COPY[browserLanguage] ? browserLanguage : "en";
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: "",
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: error?.message || "Unknown application error",
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[Mapapet] Unhandled React error:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const language = getErrorLanguage();
    const copy = ERROR_COPY[language] || ERROR_COPY.en;

    return (
      <main
        className="app-error-boundary"
        role="alert"
        lang={language}
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        <section className="app-error-boundary__card">
          <div className="app-error-boundary__logo">mapapet</div>
          <h1>{copy.title}</h1>
          <p>{copy.text}</p>

          <div className="app-error-boundary__actions">
            <button type="button" onClick={this.handleReload}>
              {copy.reload}
            </button>
            <button type="button" onClick={this.handleGoHome}>
              {copy.home}
            </button>
          </div>

          {process.env.NODE_ENV !== "production" && this.state.errorMessage && (
            <pre>{this.state.errorMessage}</pre>
          )}
        </section>
      </main>
    );
  }
}

export default AppErrorBoundary;
